import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { pdf } from '@react-pdf/renderer'
import { FileText, Lightbulb, History, Download, Loader2, FileSpreadsheet } from 'lucide-react'
import { api } from '@/lib/api-client'
import { PageHeader } from '@/components/ui/page-header'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@roi365/shared'
import { toast } from 'sonner'
import {
  ExecutiveSummaryPDF,
  type ExecutiveSummaryData,
} from '@/components/reports/executive-summary-pdf'
import {
  RecommendationsDetailPDF,
  type RecommendationsDetailData,
} from '@/components/reports/recommendations-detail-pdf'
import {
  SavingsAuditPDF,
  type SavingsAuditData,
} from '@/components/reports/savings-audit-pdf'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  AreaChart,
  Area,
  ReferenceLine,
  Label as RcLabel,
} from 'recharts'

type ReportType = 'executive' | 'detail' | 'audit'

const reportTabs: { id: ReportType; label: string; description: string; icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }> }[] = [
  {
    id: 'executive',
    label: 'Resumen Ejecutivo',
    description: 'Vision agregada, KPIs y top recomendaciones — formato cliente final.',
    icon: FileText,
  },
  {
    id: 'detail',
    label: 'Detalle de Recomendaciones',
    description: 'Listado completo filtrable con tipo, severidad, estado y fechas.',
    icon: Lightbulb,
  },
  {
    id: 'audit',
    label: 'Auditoria de Ahorros',
    description: 'Justificacion de los ahorros conseguidos en un periodo concreto.',
    icon: History,
  },
]

const ROLE_BADGES: Record<string, string> = {
  super_admin: 'Administrador Global',
  tenant_admin: 'Administrador del Cliente',
  tenant_viewer: 'Visor',
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

function oneYearAgoISO() {
  const d = new Date()
  d.setFullYear(d.getFullYear() - 1)
  return d.toISOString().slice(0, 10)
}

export function ReportsPage() {
  const [active, setActive] = useState<ReportType>('executive')

  return (
    <div className="space-y-6">
      <PageHeader
        title="Informes"
        description="Informes profesionales descargables en PDF para compartir con tu cliente o direccion."
      />

      {/* Selector de tipo de informe */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {reportTabs.map((t) => {
          const isActive = active === t.id
          return (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              className="rounded-lg border p-4 text-left transition-all hover:shadow-sm"
              style={{
                borderColor: isActive ? 'var(--color-brand-secondary)' : 'var(--color-border)',
                backgroundColor: isActive ? 'var(--color-brand-primary-50)' : 'var(--color-surface)',
                borderLeftWidth: isActive ? 4 : 1,
                borderLeftColor: isActive ? 'var(--color-brand-secondary)' : 'var(--color-border)',
              }}
            >
              <div className="flex items-center gap-2">
                <t.icon className="h-4 w-4" style={{ color: 'var(--color-brand-secondary)' }} />
                <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t.label}</p>
              </div>
              <p className="mt-2 text-xs leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                {t.description}
              </p>
            </button>
          )
        })}
      </div>

      {active === 'executive' && <ExecutiveSummarySection />}
      {active === 'detail' && <RecommendationsDetailSection />}
      {active === 'audit' && <SavingsAuditSection />}
    </div>
  )
}

// ─── Executive Summary ──────────────────────────────────────
function ExecutiveSummarySection() {
  const [downloading, setDownloading] = useState(false)
  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'executive'],
    queryFn: () => api.get<ExecutiveSummaryData>('/reports/executive-summary'),
  })

  const handleDownload = async () => {
    if (!data) return
    setDownloading(true)
    try {
      const blob = await pdf(<ExecutiveSummaryPDF data={data} />).toBlob()
      const safeName = (data.tenant?.slug ?? 'cliente').replace(/[^a-z0-9-]/gi, '-')
      downloadBlob(blob, `roi365-resumen-ejecutivo-${safeName}-${todayISO()}.pdf`)
      toast.success('Informe descargado')
    } catch (e) {
      toast.error((e as Error).message ?? 'Error generando PDF')
    } finally {
      setDownloading(false)
    }
  }

  if (isLoading || !data) {
    return <SectionSkeleton />
  }

  const k = data.kpis
  const wasteCost = k.monthlyCost - k.monthlyCostAssigned
  const utilization = k.totalPurchased > 0 ? (k.totalAssigned / k.totalPurchased) * 100 : 0

  const topByImpact = [...data.topRecommendations]
    .sort((a, b) => b.projected_saving - a.projected_saving)
    .slice(0, 6)

  return (
    <div className="space-y-4">
      <ReportToolbar
        clientName={data.tenant?.name ?? '—'}
        clientPlan={data.tenant?.plan}
        onDownload={handleDownload}
        downloading={downloading}
        downloadLabel="Descargar Resumen Ejecutivo"
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <Kpi label="Coste mensual total" value={formatCurrency(k.monthlyCost)} sub={`${formatCurrency(k.monthlyCostAssigned)} asignadas`} />
        <Kpi label="Desperdicio mensual" value={formatCurrency(wasteCost)} accent="error" />
        <Kpi label="Utilizacion" value={`${utilization.toFixed(1)}%`} sub={`${k.totalAssigned} / ${k.totalPurchased} licencias`} />
        <Kpi label="Usuarios" value={String(k.totalUsers)} sub={`${k.activeUsers30d} activos 30d`} />
        <Kpi label="Ahorro potencial estimado" value={formatCurrency(k.pendingTotalSaving)} sub={`${k.pendingCount} pendientes`} accent="warning" />
        <Kpi label="Ahorro proyectado aplicado" value={formatCurrency(k.appliedTotalSaving)} sub={`${k.appliedCount} aplicadas`} accent="success" />
      </div>

      <Card>
        <CardHeader><CardTitle>Top recomendaciones por impacto</CardTitle></CardHeader>
        {topByImpact.length === 0 ? (
          <p className="py-6 text-sm" style={{ color: 'var(--color-text-tertiary)' }}>No hay recomendaciones pendientes.</p>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topByImpact} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-200)" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
                <YAxis
                  type="category"
                  dataKey="title"
                  tick={{ fontSize: 11 }}
                  width={220}
                  tickFormatter={(v: string) => v.length > 30 ? v.slice(0, 28) + '…' : v}
                />
                <Tooltip formatter={(value: number) => [formatCurrency(value), 'Ahorro proyectado']} />
                <Bar dataKey="projected_saving" fill="var(--color-brand-secondary)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader><CardTitle>Distribucion del coste por licencia</CardTitle></CardHeader>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.costBreakdown.slice(0, 8)} layout="vertical" margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-200)" />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
              <YAxis type="category" dataKey="display_name" tick={{ fontSize: 11 }} width={200} />
              <Tooltip formatter={(value: number) => [formatCurrency(value), 'Coste total']} />
              <Bar dataKey="total_cost" fill="var(--color-brand-accent-1)" radius={[0, 4, 4, 0]}>
                {data.costBreakdown.slice(0, 8).map((_, i) => (
                  <Cell key={i} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  )
}

// ─── Recommendations Detail ─────────────────────────────────
function RecommendationsDetailSection() {
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    severity: '',
    from: '',
    to: '',
  })
  const [downloading, setDownloading] = useState(false)

  const queryString = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v) as [string, string][],
  ).toString()

  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'detail', queryString],
    queryFn: () => api.get<RecommendationsDetailData>(`/reports/recommendations-detail${queryString ? `?${queryString}` : ''}`),
  })

  const handleDownload = async () => {
    if (!data) return
    setDownloading(true)
    try {
      const blob = await pdf(<RecommendationsDetailPDF data={data} />).toBlob()
      const safeName = (data.tenant?.slug ?? 'cliente').replace(/[^a-z0-9-]/gi, '-')
      downloadBlob(blob, `roi365-detalle-recomendaciones-${safeName}-${todayISO()}.pdf`)
      toast.success('Informe descargado')
    } catch (e) {
      toast.error((e as Error).message ?? 'Error generando PDF')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="space-y-4">
      <ReportToolbar
        clientName={data?.tenant?.name ?? '—'}
        clientPlan={data?.tenant?.plan}
        onDownload={handleDownload}
        downloading={downloading || isLoading}
        downloadLabel="Descargar Detalle"
      />

      <Card>
        <CardHeader><CardTitle>Filtros</CardTitle></CardHeader>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-5">
          <FilterSelect
            label="Estado"
            value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            options={[
              { value: '', label: 'Todos' },
              { value: 'pending', label: 'Pendientes' },
              { value: 'applied', label: 'Aplicadas' },
              { value: 'dismissed', label: 'Descartadas' },
            ]}
          />
          <FilterSelect
            label="Tipo"
            value={filters.type}
            onChange={(v) => setFilters((f) => ({ ...f, type: v }))}
            options={[
              { value: '', label: 'Todos' },
              { value: 'remove_license', label: 'Eliminar licencia' },
              { value: 'downgrade', label: 'Downgrade' },
              { value: 'disable_service', label: 'Deshabilitar servicio' },
              { value: 'reduce_purchased', label: 'Reducir compradas' },
              { value: 'remove_redundant', label: 'Eliminar redundante' },
              { value: 'alert_exchange', label: 'Infrautilización Exchange' },
              { value: 'alert_onedrive', label: 'Infrautilización OneDrive' },
              { value: 'alert_sharepoint', label: 'Infrautilización SharePoint' },
              { value: 'alert_teams', label: 'Infrautilización Teams' },
              { value: 'alert_copilot', label: 'Copilot sin uso' },
            ]}
          />
          <FilterSelect
            label="Severidad"
            value={filters.severity}
            onChange={(v) => setFilters((f) => ({ ...f, severity: v }))}
            options={[
              { value: '', label: 'Todas' },
              { value: 'low', label: 'Baja' },
              { value: 'medium', label: 'Media' },
              { value: 'high', label: 'Alta' },
              { value: 'critical', label: 'Critica' },
            ]}
          />
          <DateInput
            label="Desde (creada)"
            value={filters.from}
            onChange={(v) => setFilters((f) => ({ ...f, from: v }))}
          />
          <DateInput
            label="Hasta (creada)"
            value={filters.to}
            onChange={(v) => setFilters((f) => ({ ...f, to: v }))}
          />
        </div>
      </Card>

      <Card padding={false}>
        <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: 'var(--color-border)' }}>
          <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {data?.recommendations.length ?? 0} recomendaciones
          </p>
          {data && (
            <div className="flex gap-4 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              <span>
                Mensual: <strong style={{ color: 'var(--color-text-primary)' }}>
                  {formatCurrency(data.recommendations.reduce((s, r) => s + r.estimated_monthly_saving, 0))}
                </strong>
              </span>
              <span>
                Proyectado: <strong style={{ color: 'var(--color-brand-secondary)' }}>
                  {formatCurrency(data.recommendations.reduce((s, r) => s + r.projected_saving, 0))}
                </strong>
              </span>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-gray-50)' }}>
                {['Recomendacion', 'Tipo', 'Severidad', 'Estado', 'Mensual', 'Proyectado'].map((h, i) => (
                  <th key={h} className={`px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider ${i >= 4 ? 'text-right' : ''}`} style={{ color: 'var(--color-text-secondary)' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i}><td colSpan={6} className="p-3"><div className="h-6 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} /></td></tr>
                ))
              ) : !data?.recommendations.length ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center" style={{ color: 'var(--color-text-tertiary)' }}>
                    Sin resultados con los filtros actuales
                  </td>
                </tr>
              ) : (
                data.recommendations.slice(0, 50).map((r) => (
                  <tr key={r.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                    <td className="px-4 py-2.5">
                      <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{r.title}</p>
                      <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                        {r.license_display_name ?? '—'}{r.user_display_name ? ` · ${r.user_display_name}` : ''}
                      </p>
                    </td>
                    <td className="px-4 py-2.5 text-xs">{r.type}</td>
                    <td className="px-4 py-2.5 text-xs font-semibold capitalize">{r.severity}</td>
                    <td className="px-4 py-2.5 text-xs font-semibold capitalize">{r.status}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(r.estimated_monthly_saving)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold" style={{ color: 'var(--color-brand-secondary)' }}>
                      {formatCurrency(r.projected_saving)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {data && data.recommendations.length > 50 && (
          <div className="border-t px-4 py-2 text-center text-xs" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-tertiary)' }}>
            Mostrando primeras 50 — descarga el PDF para ver el listado completo ({data.recommendations.length}).
          </div>
        )}
      </Card>
    </div>
  )
}

// ─── Savings Audit ──────────────────────────────────────────
function SavingsAuditSection() {
  const [period, setPeriod] = useState({ from: oneYearAgoISO(), to: todayISO() })
  const [downloading, setDownloading] = useState(false)
  const queryString = new URLSearchParams({ from: period.from, to: period.to }).toString()

  // Cuántos días cubre el período para alimentar la grafica (cap. backend 7-730).
  const periodDays = (() => {
    const ms = new Date(period.to).getTime() - new Date(period.from).getTime()
    const days = Math.ceil(ms / (1000 * 60 * 60 * 24))
    return Math.min(Math.max(days, 7), 730)
  })()

  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'audit', queryString],
    queryFn: () => api.get<SavingsAuditData>(`/reports/savings-audit?${queryString}`),
  })

  const { data: evolution } = useQuery({
    queryKey: ['dashboard', 'savings-evolution', periodDays],
    queryFn: () => api.get<{ series: { snapshot_date: string; cumulative_applied_saving: number }[]; currentPendingSaving: number }>(`/dashboard/savings-evolution?days=${periodDays}`),
  })

  const handleDownload = async () => {
    if (!data) return
    setDownloading(true)
    try {
      const blob = await pdf(<SavingsAuditPDF data={data} />).toBlob()
      const safeName = (data.tenant?.slug ?? 'cliente').replace(/[^a-z0-9-]/gi, '-')
      downloadBlob(blob, `roi365-auditoria-ahorros-${safeName}-${todayISO()}.pdf`)
      toast.success('Informe descargado')
    } catch (e) {
      toast.error((e as Error).message ?? 'Error generando PDF')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="space-y-4">
      <ReportToolbar
        clientName={data?.tenant?.name ?? '—'}
        clientPlan={data?.tenant?.plan}
        onDownload={handleDownload}
        downloading={downloading || isLoading}
        downloadLabel="Descargar Auditoria"
      />

      <Card>
        <CardHeader><CardTitle>Periodo</CardTitle></CardHeader>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <DateInput label="Desde" value={period.from} onChange={(v) => setPeriod((p) => ({ ...p, from: v }))} />
          <DateInput label="Hasta" value={period.to} onChange={(v) => setPeriod((p) => ({ ...p, to: v }))} />
          <div className="flex items-end gap-2">
            {[
              { label: 'Ult. mes', from: addMonths(-1) },
              { label: 'Ult. trimestre', from: addMonths(-3) },
              { label: 'Ult. año', from: oneYearAgoISO() },
            ].map((preset) => {
              const today = todayISO()
              const isActive = period.from === preset.from && period.to === today
              return (
                <Button
                  key={preset.label}
                  variant={isActive ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setPeriod({ from: preset.from, to: today })}
                >
                  {preset.label}
                </Button>
              )
            })}
          </div>
        </div>
      </Card>

      {data && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Kpi label="Aplicadas en periodo" value={String(data.kpis.appliedCount)} sub={`${formatCurrency(data.kpis.appliedTotalSaving)} proyectado`} accent="success" />
          <Kpi label="Descartadas en periodo" value={String(data.kpis.dismissedCount)} sub={`${formatCurrency(data.kpis.dismissedTotalSaving)} proyectado`} accent="default" />
          <Kpi label="Pendientes (actuales)" value={String(data.kpis.pendingCount)} sub={`${formatCurrency(data.kpis.pendingTotalSaving)} proyectado`} accent="warning" />
        </div>
      )}

      <Card>
        <CardHeader><CardTitle>Evolucion del Ahorro Acumulado</CardTitle></CardHeader>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={(evolution?.series ?? []).map((s) => ({
                date: s.snapshot_date.slice(5),
                acumulado: Math.round(s.cumulative_applied_saving * 100) / 100,
              }))}
              margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-gray-200)" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `${v}€`}
                domain={[0, (dataMax: number) => Math.ceil(Math.max(dataMax, evolution?.currentPendingSaving ?? 0) * 1.1)]}
              />
              <Tooltip formatter={(value: number) => [formatCurrency(value), 'Ahorro acumulado']} />
              <Area type="monotone" dataKey="acumulado" stroke="var(--color-success)" fill="var(--color-success-light)" strokeWidth={2} />
              {evolution?.currentPendingSaving ? (
                <ReferenceLine
                  y={evolution.currentPendingSaving}
                  stroke="var(--color-warning)"
                  strokeDasharray="6 4"
                  strokeWidth={2}
                  ifOverflow="extendDomain"
                >
                  <RcLabel
                    value={`Potencial pendiente: ${formatCurrency(evolution.currentPendingSaving)}`}
                    position="insideTopRight"
                    style={{ fill: 'var(--color-warning)', fontSize: 11, fontWeight: 600 }}
                  />
                </ReferenceLine>
              ) : null}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card padding={false}>
        <div className="border-b px-4 py-3" style={{ borderColor: 'var(--color-border)' }}>
          <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Cronologia</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-gray-50)' }}>
                {['Fecha', 'Estado', 'Recomendacion', 'Tipo', 'Mensual', 'Proyectado'].map((h, i) => (
                  <th key={h} className={`px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider ${i >= 4 ? 'text-right' : ''}`} style={{ color: 'var(--color-text-secondary)' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!data || data.timeline.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center" style={{ color: 'var(--color-text-tertiary)' }}>
                    Sin acciones registradas en este periodo
                  </td>
                </tr>
              ) : (
                data.timeline.slice(0, 50).map((r) => (
                  <tr key={r.id} className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                    <td className="px-4 py-2.5 text-xs">{(r.applied_at ?? r.dismissed_at ?? '').slice(0, 10)}</td>
                    <td className="px-4 py-2.5 text-xs font-semibold capitalize">{r.status}</td>
                    <td className="px-4 py-2.5">
                      <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{r.title}</p>
                      <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{r.license_display_name ?? '—'}</p>
                    </td>
                    <td className="px-4 py-2.5 text-xs">{r.type}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(r.estimated_monthly_saving)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold" style={{ color: r.status === 'applied' ? 'var(--color-success)' : 'var(--color-text-tertiary)' }}>
                      {r.status === 'applied' ? formatCurrency(r.projected_saving) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {data && data.timeline.length > 50 && (
          <div className="border-t px-4 py-2 text-center text-xs" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-tertiary)' }}>
            Mostrando primeras 50 — descarga el PDF para ver el cronologico completo ({data.timeline.length}).
          </div>
        )}
      </Card>
    </div>
  )
}

// ─── Helpers UI ─────────────────────────────────────────────
function ReportToolbar({
  clientName,
  clientPlan,
  onDownload,
  downloading,
  downloadLabel,
}: {
  clientName: string
  clientPlan?: string
  onDownload: () => void
  downloading: boolean
  downloadLabel: string
}) {
  return (
    <Card>
      <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
        <div>
          <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>Cliente</p>
          <p className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{clientName}</p>
          {clientPlan && (
            <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>Plan: {clientPlan}</p>
          )}
        </div>
        <Button onClick={onDownload} disabled={downloading}>
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {downloadLabel}
        </Button>
      </div>
    </Card>
  )
}

function Kpi({
  label,
  value,
  sub,
  accent = 'default',
}: {
  label: string
  value: string
  sub?: string
  accent?: 'default' | 'success' | 'warning' | 'error'
}) {
  const borderColor =
    accent === 'success' ? 'var(--color-success)'
    : accent === 'warning' ? 'var(--color-warning)'
    : accent === 'error' ? 'var(--color-error)'
    : 'var(--color-brand-secondary)'

  return (
    <div className="rounded-lg border p-4" style={{ borderColor: 'var(--color-border)', borderLeftWidth: 4, borderLeftColor: borderColor, backgroundColor: 'var(--color-surface)' }}>
      <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>{label}</p>
      <p className="mt-1 text-xl font-bold tabular-nums" style={{ color: 'var(--color-text-primary)' }}>{value}</p>
      {sub && <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{sub}</p>}
    </div>
  )
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border px-3 py-2 text-sm"
        style={{ borderColor: 'var(--color-border)' }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  )
}

function DateInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>{label}</label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border px-3 py-2 text-sm"
        style={{ borderColor: 'var(--color-border)' }}
      />
    </div>
  )
}

function SectionSkeleton() {
  return (
    <div className="space-y-4">
      <div className="h-24 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-100)' }} />
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-100)' }} />
        ))}
      </div>
      <div className="h-72 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-100)' }} />
    </div>
  )
}

function addMonths(months: number): string {
  const d = new Date()
  d.setMonth(d.getMonth() + months)
  return d.toISOString().slice(0, 10)
}

// suppress unused
void ROLE_BADGES
void FileSpreadsheet
